import React, { useEffect, useState } from "react";
import { AdminShell } from "../components/AdminShell";
import { api } from "../lib/api";
import { toast } from "sonner";
import { Download, Trash2, Mail, Send, Users, Sparkles, Bell } from "lucide-react";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from "../components/ui/alert-dialog";
import { Input } from "../components/ui/input";
import { Textarea } from "../components/ui/textarea";
import { Label } from "../components/ui/label";

export default function AdminNewsletter() {
  const [subs, setSubs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [subject, setSubject] = useState("");
  const [body, setBody] = useState("");
  const [sending, setSending] = useState(false);
  const [pushCount, setPushCount] = useState(0);
  const [pushTitle, setPushTitle] = useState("");
  const [pushBody, setPushBody] = useState("");
  const [pushUrl, setPushUrl] = useState("/");
  const [pushSending, setPushSending] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const [r, p] = await Promise.all([
        api.get("/newsletter/subscribers"),
        api.get("/push/subscribers-count").catch(() => ({ data: { count: 0 } })),
      ]);
      setSubs(r.data);
      setPushCount(p.data.count || 0);
    } catch {
      toast.error("Errore caricamento iscritti");
    } finally {
      setLoading(false);
    }
  };

  const sendPush = async () => {
    if (!pushTitle.trim() || !pushBody.trim()) {
      toast.error("Compila titolo e messaggio");
      return;
    }
    if (!window.confirm(`Inviare notifica push a ${pushCount} dispositivi?`)) return;
    setPushSending(true);
    try {
      const r = await api.post("/push/broadcast", {
        title: pushTitle,
        body: pushBody,
        url: pushUrl || "/",
      });
      const stale = r.data.removed_stale ? ` (${r.data.removed_stale} stale rimossi)` : "";
      toast.success(`Push inviate: ${r.data.sent}/${r.data.total}${stale}`);
      setPushTitle("");
      setPushBody("");
      load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Errore invio push");
    } finally {
      setPushSending(false);
    }
  };

  useEffect(() => { load(); }, []);

  const remove = async (id) => {
    try {
      await api.delete(`/newsletter/subscribers/${id}`);
      toast.success("Iscritto rimosso");
      load();
    } catch { toast.error("Errore rimozione"); }
  };

  const exportCsv = () => {
    const rows = [["email", "source", "created_at"], ...subs.map((s) => [s.email, s.source || "", s.created_at])];
    const csv = rows.map((r) => r.map((v) => `"${(v || "").replace(/"/g, '""')}"`).join(",")).join("\n");
    const blob = new Blob([csv], { type: "text/csv" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `guzzardella-newsletter-${new Date().toISOString().slice(0, 10)}.csv`;
    a.click();
    URL.revokeObjectURL(url);
    toast.success(`${subs.length} iscritti esportati`);
  };

  const broadcast = async () => {
    if (!subject.trim() || !body.trim()) {
      toast.error("Compila oggetto e corpo del messaggio");
      return;
    }
    if (!window.confirm(`Inviare "${subject}" a ${subs.length} iscritti?`)) return;
    setSending(true);
    try {
      const paragraphs = body.split(/\n\n+/).map((p) => `<p style="line-height:1.7;font-size:14px;margin:0 0 16px;">${p.replace(/\n/g, "<br/>")}</p>`).join("");
      const html = `<h2 style="font-family:Georgia,serif;margin:0 0 20px;color:#0f172a;">${subject}</h2>${paragraphs}<p style="line-height:1.7;font-size:13px;margin:24px 0 0;color:#666;">A presto,<br/>Rocco · Guzzardella Auto</p>`;
      const r = await api.post("/newsletter/broadcast", { subject, html_body: html });
      toast.success(`${r.data.sent}/${r.data.total} email inviate${r.data.note ? " · " + r.data.note : ""}`);
      setSubject("");
      setBody("");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Errore invio broadcast");
    } finally {
      setSending(false);
    }
  };

  return (
    <AdminShell title="Newsletter">
      <div className="grid grid-cols-1 lg:grid-cols-5 gap-8" data-testid="newsletter-page">
        <div className="lg:col-span-3 space-y-6">
          <div className="bg-midnight-light border border-white/5 p-6">
            <div className="flex items-center gap-3 mb-6">
              <Sparkles size={20} className="text-gold" />
              <h3 className="font-display text-xl font-semibold text-gold">Invia comunicazione</h3>
            </div>
            <p className="text-xs text-white/50 mb-6 leading-relaxed">
              Manda una email a tutti gli iscritti alla newsletter (es. "Nuova Fiat 500 arrivata in concessionaria").
              Le email vengono inviate solo se Resend è configurato sul server.
            </p>
            <div className="space-y-4">
              <div>
                <Label className="text-xs uppercase tracking-widest text-white/60">Oggetto</Label>
                <Input
                  value={subject}
                  onChange={(e) => setSubject(e.target.value)}
                  placeholder="Es. Nuova Audi A6 disponibile"
                  className="mt-2 bg-white/5 border-white/10 rounded-sm text-white"
                  data-testid="broadcast-subject"
                />
              </div>
              <div>
                <Label className="text-xs uppercase tracking-widest text-white/60">Messaggio</Label>
                <Textarea
                  value={body}
                  onChange={(e) => setBody(e.target.value)}
                  rows={10}
                  placeholder={"Ciao,\n\nÈ arrivata una nuova auto nel nostro parco: Audi A6 del 2018, 87.000 km, ottime condizioni.\n\nVieni a vederla o rispondi a questa email per informazioni."}
                  className="mt-2 bg-white/5 border-white/10 rounded-sm text-white text-sm leading-relaxed"
                  data-testid="broadcast-body"
                />
                <p className="text-[10px] text-white/40 mt-2">Righe vuote = nuovi paragrafi. Verrà aggiunta la firma automatica.</p>
              </div>
              <button
                onClick={broadcast}
                disabled={sending || !subject.trim() || !body.trim() || subs.length === 0}
                className="btn-gold w-full flex items-center justify-center gap-2 py-3 text-xs uppercase tracking-widest font-semibold disabled:opacity-50"
                data-testid="broadcast-send-btn"
              >
                <Send size={14} />
                {sending ? "Invio..." : `Invia a ${subs.length} iscritti`}
              </button>
            </div>
          </div>

          <div className="bg-midnight-light border border-white/5 p-6" data-testid="push-panel">
            <div className="flex items-center gap-3 mb-6">
              <Bell size={20} className="text-gold" />
              <h3 className="font-display text-xl font-semibold text-gold">Notifica Push</h3>
              <span className="ml-auto text-xs text-white/50" data-testid="push-count">
                {pushCount} {pushCount === 1 ? "dispositivo" : "dispositivi"}
              </span>
            </div>
            <p className="text-xs text-white/50 mb-6 leading-relaxed">
              Invia una notifica istantanea a tutti gli utenti che hanno installato la PWA e abilitato le push
              (arriva sul telefono anche ad app chiusa).
            </p>
            <div className="space-y-4">
              <div>
                <Label className="text-xs uppercase tracking-widest text-white/60">Titolo</Label>
                <Input
                  value={pushTitle}
                  onChange={(e) => setPushTitle(e.target.value)}
                  placeholder="Es. Nuova Audi A6 in concessionaria"
                  maxLength={60}
                  className="mt-2 bg-white/5 border-white/10 rounded-sm text-white"
                  data-testid="push-title"
                />
              </div>
              <div>
                <Label className="text-xs uppercase tracking-widest text-white/60">Messaggio</Label>
                <Textarea
                  value={pushBody}
                  onChange={(e) => setPushBody(e.target.value)}
                  rows={3}
                  maxLength={140}
                  placeholder="Vieni a vederla: 2018, 87.000 km, ottime condizioni."
                  className="mt-2 bg-white/5 border-white/10 rounded-sm text-white text-sm"
                  data-testid="push-body"
                />
                <p className="text-[10px] text-white/40 mt-2">Max 140 caratteri per una buona resa mobile.</p>
              </div>
              <div>
                <Label className="text-xs uppercase tracking-widest text-white/60">URL destinazione (opzionale)</Label>
                <Input
                  value={pushUrl}
                  onChange={(e) => setPushUrl(e.target.value)}
                  placeholder="/"
                  className="mt-2 bg-white/5 border-white/10 rounded-sm text-white"
                  data-testid="push-url"
                />
                <p className="text-[10px] text-white/40 mt-2">Es. "/" per la home o "/cars/ID" per un'auto specifica.</p>
              </div>
              <button
                onClick={sendPush}
                disabled={pushSending || !pushTitle.trim() || !pushBody.trim() || pushCount === 0}
                className="btn-gold w-full flex items-center justify-center gap-2 py-3 text-xs uppercase tracking-widest font-semibold disabled:opacity-50"
                data-testid="push-send-btn"
              >
                <Bell size={14} />
                {pushSending ? "Invio..." : `Invia push a ${pushCount} dispositivi`}
              </button>
            </div>
          </div>
        </div>

        <div className="lg:col-span-2 space-y-6">
          <div className="bg-midnight-light border border-white/5 p-6">
            <div className="flex items-center justify-between mb-6">
              <div className="flex items-center gap-3">
                <Users size={20} className="text-gold" />
                <h3 className="font-display text-xl font-semibold text-gold">Iscritti</h3>
              </div>
              <button
                onClick={exportCsv}
                disabled={subs.length === 0}
                className="inline-flex items-center gap-2 px-3 py-2 text-[10px] uppercase tracking-widest font-semibold border border-white/20 hover:border-gold text-white/80 hover:text-gold disabled:opacity-40"
                data-testid="export-newsletter-btn"
              >
                <Download size={12} /> CSV
              </button>
            </div>

            {loading ? (
              <p className="text-white/50 text-sm">Caricamento...</p>
            ) : subs.length === 0 ? (
              <div className="text-center py-8" data-testid="newsletter-empty">
                <Mail size={32} className="text-gold/40 mx-auto mb-3" />
                <p className="text-white/60 text-sm mb-2">Nessun iscritto ancora</p>
                <p className="text-white/40 text-xs">L'opt-in è attivo nel footer del sito</p>
              </div>
            ) : (
              <>
                <p className="text-xs text-white/50 mb-4">
                  {subs.length} {subs.length === 1 ? "iscritto" : "iscritti"} totali
                </p>
                <div className="space-y-2 max-h-[500px] overflow-y-auto">
                  {subs.map((s) => (
                    <div
                      key={s.id}
                      className="flex items-center justify-between gap-2 p-3 border border-white/5 bg-white/5 hover:border-gold/40 transition-colors"
                      data-testid={`subscriber-${s.id}`}
                    >
                      <div className="min-w-0">
                        <p className="text-sm text-white truncate">{s.email}</p>
                        <p className="text-[10px] text-white/40">
                          {new Date(s.created_at).toLocaleString("it-IT")} · da {s.source || "?"}
                        </p>
                      </div>
                      <AlertDialog>
                        <AlertDialogTrigger asChild>
                          <button
                            className="p-1.5 text-destructive/70 hover:text-destructive hover:bg-destructive/10 shrink-0"
                            data-testid={`remove-subscriber-${s.id}`}
                            aria-label="Rimuovi iscritto"
                          >
                            <Trash2 size={13} />
                          </button>
                        </AlertDialogTrigger>
                        <AlertDialogContent>
                          <AlertDialogHeader>
                            <AlertDialogTitle>Rimuovere {s.email}?</AlertDialogTitle>
                            <AlertDialogDescription>Non riceverà più le comunicazioni newsletter.</AlertDialogDescription>
                          </AlertDialogHeader>
                          <AlertDialogFooter>
                            <AlertDialogCancel>Annulla</AlertDialogCancel>
                            <AlertDialogAction onClick={() => remove(s.id)} className="bg-destructive text-white">
                              Rimuovi
                            </AlertDialogAction>
                          </AlertDialogFooter>
                        </AlertDialogContent>
                      </AlertDialog>
                    </div>
                  ))}
                </div>
              </>
            )}
          </div>
        </div>
      </div>
    </AdminShell>
  );
}
